import { Injectable, inject } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter, debounceTime } from "rxjs/operators";
import { AuthFlowService } from "../../auth-flow/auth-flow.service";
import { 
  getPrimaryRoute,
  canAccessPath,
  getFallbackRoutes,
  getRoleNavigation,
  BLACKLISTED_ROUTES 
} from "../../../config/navigation-config";

@Injectable({
  providedIn: "root",
})
export class NavigationService {
  private readonly _router = inject(Router);
  private readonly _authFlowService = inject(AuthFlowService);

  private readonly _recentNavigations: string[] = [];
  private readonly _maxRecentNavigations = 5;
  private readonly _redirectTimeWindow = 2000; // 2 seconds
  private _lastRedirectTime = 0;

  constructor() {
    // Track navigation history to detect potential loops
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(100), // Debounce to avoid rapid fire updates
      )
      .subscribe((event: NavigationEnd) => {
        this._addToHistory(event.urlAfterRedirects || event.url);
      });
  }

  /**
   * Get the best fallback route for the current user
   * Returns the previous valid route or the first available route for the user's role
   */
  getFallbackRoute(): string {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return "/auth-flow"; // Changed to auth-flow instead of complete-registration
    }

    // Try to use the previous valid route (avoid immediate loops)
    const previousRoute = this._getPreviousValidRoute();
    if (previousRoute) {
      return previousRoute;
    }

    // Get fallback routes for this role
    const fallbackRoutes = getFallbackRoutes(userRole, this._recentNavigations);
    
    // Return the first available fallback route
    if (fallbackRoutes.length > 0) {
      return fallbackRoutes[0];
    }

    // Final fallback - get primary route for role
    return getPrimaryRoute(userRole);
  }

  /**
   * Navigate to fallback route with loop prevention
   */
  navigateToFallback(currentUrl?: string): boolean {
    const now = Date.now();

    // Prevent rapid redirects
    if (now - this._lastRedirectTime < this._redirectTimeWindow) {
      console.warn(
        "[NavigationService] Preventing rapid redirect, possible loop detected",
      );
      return false;
    }

    const fallbackRoute = this.getFallbackRoute();

    // Don't redirect to the same route
    if (
      currentUrl &&
      this._normalizeUrl(currentUrl) === this._normalizeUrl(fallbackRoute)
    ) {
      console.warn(
        "[NavigationService] Would redirect to same route, aborting",
      );
      return false;
    }

    // Check if we've been to this route recently (potential loop)
    if (this._isRecentlyVisited(fallbackRoute)) {
      console.warn(
        "[NavigationService] Route recently visited, finding alternative",
      );
      const alternative = this._getAlternativeRoute(fallbackRoute);
      if (alternative && alternative !== fallbackRoute) {
        this._lastRedirectTime = now;
        this._router.navigate([alternative]);
        return true;
      }
      return false;
    }

    this._lastRedirectTime = now;
    this._router.navigate([fallbackRoute]);
    return true;
  }

  /**
   * Check if user can access the given route
   */
  canUserAccessRoute(path: string): boolean {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return false;
    }
    return canAccessPath(path, userRole);
  }
  
  /**
   * Get navigation items for current user's role
   */
  getUserNavigation() {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return [];
    }
    return getRoleNavigation(userRole);
  }
  
  /**
   * Get primary route for current user's role
   */
  getPrimaryRouteForUser(): string {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return '/auth-flow';
    }
    return getPrimaryRoute(userRole);
  }
  
  /**
   * Get all allowed paths for current user's role
   */
  getAllowedPathsForUser(): string[] {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return [];
    }
    return getRoleNavigation(userRole).map(item => item.path);
  }

  /**
   * Add URL to navigation history
   */
  private _addToHistory(url: string): void {
    const normalizedUrl = this._normalizeUrl(url);

    // Remove if already exists to move it to the end
    const existingIndex = this._recentNavigations.indexOf(normalizedUrl);
    if (existingIndex > -1) {
      this._recentNavigations.splice(existingIndex, 1);
    }

    this._recentNavigations.push(normalizedUrl);

    // Keep only recent navigations
    if (this._recentNavigations.length > this._maxRecentNavigations) {
      this._recentNavigations.shift();
    }
  }

  /**
   * Get the previous valid route that the user can access
   */
  private _getPreviousValidRoute(): string | null {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return null;
    }

    // Look at recent navigations in reverse order (most recent first)
    for (let i = this._recentNavigations.length - 2; i >= 0; i--) {
      const url = this._recentNavigations[i];

      if (
        !BLACKLISTED_ROUTES.includes(url) &&
        canAccessPath(url, userRole)
      ) {
        return url;
      }
    }

    return null;
  }

  /**
   * Check if a route was recently visited
   */
  private _isRecentlyVisited(url: string): boolean {
    const normalizedUrl = this._normalizeUrl(url);
    const recentCount = this._recentNavigations.filter(
      (recent) => recent === normalizedUrl,
    ).length;
    return recentCount >= 2; // Consider it a loop if visited 2+ times recently
  }

  /**
   * Get an alternative route when the primary fallback might cause a loop
   */
  private _getAlternativeRoute(problematicRoute: string): string | null {
    const userRole = this._authFlowService.userRole();
    if (!userRole) {
      return null;
    }

    const roleNavigation = getRoleNavigation(userRole);

    // Find the second available route that's not the problematic one
    const alternatives = roleNavigation.filter(
      (nav) =>
        nav.path !== problematicRoute &&
        !BLACKLISTED_ROUTES.includes(nav.path) &&
        !this._isRecentlyVisited(nav.path) &&
        canAccessPath(nav.path, userRole),
    );

    return alternatives.length > 0 ? alternatives[0].path : null;
  }

  /**
   * Get role-specific fallback when all else fails
   */
  private _getRoleSpecificFallback(userRole: string): string {
    // Simply return the primary route for the role
    // The navigation config already handles priority and role-specific routing
    return getPrimaryRoute(userRole);
  }

  /**
   * Normalize URL for comparison (remove query params, fragments, etc.)
   */
  private _normalizeUrl(url: string): string {
    return url.split("?")[0].split("#")[0].toLowerCase();
  }
}
